import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Download, Trash2, Edit3, Ban, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function GDPRPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-semibold">GDPR</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none">
          <h1 className="text-3xl font-bold mb-2">GDPR & Your Data Rights</h1>
          <p className="text-muted-foreground mb-4">Last Updated: January 7, 2024</p>
          <p className="text-muted-foreground mb-8">
            This page explains your rights under the General Data Protection Regulation (GDPR) and how YASNO AI protects and handles your personal data.
          </p>

          {/* Quick Summary Box */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4 text-foreground">🇪🇺 Your Rights at a Glance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Access</p>
                  <p className="text-sm text-muted-foreground">Request a copy of your data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Edit3 className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Rectification</p>
                  <p className="text-sm text-muted-foreground">Correct inaccurate data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Trash2 className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Erasure</p>
                  <p className="text-sm text-muted-foreground">Request deletion of your data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Portability</p>
                  <p className="text-sm text-muted-foreground">Export your data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Ban className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Object</p>
                  <p className="text-sm text-muted-foreground">Opt out of certain processing</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Right to Restrict</p>
                  <p className="text-sm text-muted-foreground">Limit how we use your data</p>
                </div>
              </div>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">1. Who We Are</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              YASNO Marketing LLC ("we," "us," or "our") is the data controller responsible for your personal data. We are committed to protecting your privacy and ensuring your data is handled in accordance with the GDPR.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-muted-foreground"><strong>Data Controller:</strong> YASNO Marketing LLC</p>
              <p className="text-muted-foreground"><strong>Contact:</strong> support@yasnomktg.com</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">2. Legal Basis for Processing</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We process your personal data under the following legal bases as defined by the GDPR:
            </p>
            
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">📜 Contractual Necessity (Article 6(1)(b))</h3>
                <p className="text-muted-foreground text-sm">Processing necessary to provide our services to you, including account management, campaign execution, and customer support.</p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">✅ Consent (Article 6(1)(a))</h3>
                <p className="text-muted-foreground text-sm">Processing based on your explicit consent, such as marketing communications, AI agent authorization, and optional analytics.</p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">⚖️ Legitimate Interests (Article 6(1)(f))</h3>
                <p className="text-muted-foreground text-sm">Processing necessary for our legitimate business interests, such as fraud prevention, security, and service improvement, balanced against your rights.</p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">📋 Legal Obligation (Article 6(1)(c))</h3>
                <p className="text-muted-foreground text-sm">Processing required to comply with legal obligations, such as tax records, fraud prevention, and responding to lawful requests.</p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">3. Your GDPR Rights Explained</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.1 Right of Access (Article 15)</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  You have the right to obtain confirmation as to whether we process your personal data and, if so, access to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>The categories of personal data we process</li>
                  <li>The purposes of processing</li>
                  <li>Recipients or categories of recipients</li>
                  <li>The retention period or criteria used to determine it</li>
                  <li>Information about the source of the data</li>
                  <li>The existence of automated decision-making, including profiling</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.2 Right to Rectification (Article 16)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You have the right to have inaccurate personal data corrected without undue delay. You may also complete incomplete personal data by providing a supplementary statement.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.3 Right to Erasure / "Right to be Forgotten" (Article 17)</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  You may request deletion of your personal data when:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>The data is no longer necessary for its original purpose</li>
                  <li>You withdraw consent and there is no other legal basis</li>
                  <li>You object to processing and there are no overriding legitimate grounds</li>
                  <li>The data has been unlawfully processed</li>
                  <li>Erasure is required for legal compliance</li>
                </ul>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mt-3">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> We may retain certain data for legal compliance, dispute resolution, or legitimate business purposes even after a deletion request.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.4 Right to Restrict Processing (Article 18)</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  You may request restriction of processing when:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>You contest the accuracy of the data (pending verification)</li>
                  <li>Processing is unlawful but you prefer restriction over erasure</li>
                  <li>We no longer need the data but you need it for legal claims</li>
                  <li>You have objected to processing (pending verification of legitimate grounds)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.5 Right to Data Portability (Article 20)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You have the right to receive your personal data in a structured, commonly used, machine-readable format (e.g., JSON, CSV) and transmit it to another controller. This applies to data you provided to us and processed based on consent or contract.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.6 Right to Object (Article 21)</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  You have the right to object to processing based on legitimate interests or for direct marketing purposes. When you object to direct marketing, we will stop processing immediately.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2 text-foreground">3.7 Rights Related to Automated Decision-Making (Article 22)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You have the right not to be subject to decisions based solely on automated processing, including profiling, that produce legal effects or significantly affect you. You may request human intervention, express your point of view, and contest such decisions.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">4. How to Exercise Your Rights</h2>
            
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-medium mb-4 text-foreground">📧 Submit a Data Request</h3>
              <p className="text-muted-foreground mb-4">To exercise any of your GDPR rights, please contact us:</p>
              <div className="space-y-2">
                <p className="text-muted-foreground"><strong>Email:</strong> <a href="mailto:support@yasnomktg.com" className="text-primary hover:underline">support@yasnomktg.com</a></p>
                <p className="text-muted-foreground"><strong>Subject Line:</strong> "GDPR Data Request - [Your Right]"</p>
              </div>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">4.1 What to Include in Your Request</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Your full name and email address associated with your account</li>
              <li>The specific right you wish to exercise</li>
              <li>Any relevant details to help us locate your data</li>
              <li>Proof of identity (we may request this for verification)</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 text-foreground">4.2 Response Timeline</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">
              We will respond to your request within <strong>30 days</strong>. This period may be extended by an additional 60 days for complex requests, in which case we will inform you within the first 30 days.
            </p>

            <h3 className="text-lg font-medium mb-3 text-foreground">4.3 Fees</h3>
            <p className="text-muted-foreground leading-relaxed">
              Exercising your GDPR rights is free of charge. However, we may charge a reasonable fee for manifestly unfounded, excessive, or repetitive requests.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">5. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Your data may be transferred to and processed in countries outside the European Economic Area (EEA). When this occurs, we ensure appropriate safeguards are in place:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection (e.g., UK, Switzerland, Canada)</li>
              <li><strong>Standard Contractual Clauses (SCCs):</strong> EU-approved contractual safeguards for transfers to other countries</li>
              <li><strong>Binding Corporate Rules:</strong> For transfers within our corporate group</li>
              <li><strong>Consent:</strong> In specific situations, with your explicit consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-muted-foreground">Data Category</th>
                    <th className="text-left py-2 text-muted-foreground">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-white/5">
                    <td className="py-2">Account Information</td>
                    <td className="py-2">Duration of account + 30 days</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-2">Campaign Data</td>
                    <td className="py-2">Duration of account + 90 days</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-2">Payment Records</td>
                    <td className="py-2">7 years (legal requirement)</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-2">Support Communications</td>
                    <td className="py-2">3 years</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-2">Marketing Preferences</td>
                    <td className="py-2">Until withdrawn</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-2">Analytics Data</td>
                    <td className="py-2">26 months (anonymized after)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">7. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal data:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
              <li>Regular backups and disaster recovery plans</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">8. Data Breach Notification</h2>
            <p className="text-muted-foreground leading-relaxed">
              In the event of a personal data breach that poses a risk to your rights and freedoms, we will notify the relevant supervisory authority within 72 hours and notify you without undue delay if the breach is likely to result in a high risk to your rights and freedoms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">9. Right to Lodge a Complaint</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you believe we have not handled your personal data properly, you have the right to lodge a complaint with a supervisory authority. You may contact:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>The supervisory authority in your country of residence</li>
              <li>The supervisory authority in your country of work</li>
              <li>The supervisory authority where the alleged infringement occurred</li>
            </ul>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-4">
              <p className="text-muted-foreground text-sm">
                <strong>Example EU Supervisory Authorities:</strong><br/>
                • Ireland: Data Protection Commission (dataprotection.ie)<br/>
                • Germany: BfDI (bfdi.bund.de)<br/>
                • France: CNIL (cnil.fr)<br/>
                • UK: ICO (ico.org.uk)
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">10. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">For any questions about this policy or your data rights:</p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-2">
              <p className="text-muted-foreground">YASNO Marketing LLC</p>
              <p className="text-muted-foreground">Email: support@yasnomktg.com</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">11. Related Policies</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">For more information, please see:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-primary hover:underline">Terms of Service</Link></li>
              <li><Link to="/cookies" className="text-primary hover:underline">Cookie Policy</Link></li>
            </ul>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Button
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="rounded-full"
          >
            Back to Top
          </Button>
        </div>
      </main>
    </div>
  );
}
